import { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, MessageCircle, Loader2, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { BRAND, whatsappUrl } from '@/constants/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
    'General Consultation',
    'Financial Planning',
    'Wealth Management',
    'Retirement Planning',
    'Investment Advisory',
    'Tax Planning',
    'Insurance Planning',
    "Children's Education Planning",
];

export default function Contact() {
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        service: SERVICE_OPTIONS[0],
        message: '',
    });
    const [status, setStatus] = useState({ loading: false, ok: false, error: '' });

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone) {
            setStatus({ loading: false, ok: false, error: 'Please fill name, email and phone.' });
            return;
        }
        setStatus({ loading: true, ok: false, error: '' });
        try {
            const { data } = await axios.post(`${API}/consultations`, form);
            setStatus({ loading: false, ok: true, error: '' });
            toast.success('Thank you. We will reach out within 1 business day.');
            setForm({ name: '', email: '', phone: '', service: SERVICE_OPTIONS[0], message: '' });
            // optional: log id
            console.log('consultation created', data?.id);
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Could not submit. Please try again or call us directly.';
            setStatus({ loading: false, ok: false, error: detail });
            toast.error(detail);
        }
    };

    return (
        <section id="contact" className="section-pad bg-white">
            <div className="container-wl grid lg:grid-cols-12 gap-12 lg:gap-16">
                {/* LEFT — info */}
                <div className="lg:col-span-5">
                    <div className="flex items-center gap-3 mb-5">
                        <span className="gold-rule" />
                        <span className="overline">Book a Consultation</span>
                    </div>
                    <h2 className="font-display-bold text-ink text-4xl lg:text-[52px] leading-[1.05] tracking-tight">
                        Let’s start with a <span className="italic text-gold-600">conversation.</span>
                    </h2>
                    <p className="mt-5 text-ink/70 text-[16px] leading-relaxed">
                        Share a few details and one of our founders will personally reach out within one
                        business day. No pressure, no jargon — just a focused first conversation.
                    </p>

                    <div className="mt-10 space-y-5">
                        <a
                            href={`tel:${BRAND.phones[0].replace(/\s/g, '')}`}
                            className="flex items-start gap-4 group"
                        >
                            <div className="w-11 h-11 flex items-center justify-center bg-soft border border-hairline group-hover:bg-gold-50 group-hover:border-gold/40 transition-colors">
                                <Phone className="w-4 h-4 text-gold-600" />
                            </div>
                            <div>
                                <p className="text-[11px] uppercase tracking-widest2 text-ink/55">Call us</p>
                                <p className="text-[15px] text-ink font-medium mt-0.5">{BRAND.phones[0]}</p>
                                <p className="text-[15px] text-ink font-medium">{BRAND.phones[1]}</p>
                            </div>
                        </a>

                        <a href={`mailto:${BRAND.email}`} className="flex items-start gap-4 group">
                            <div className="w-11 h-11 flex items-center justify-center bg-soft border border-hairline group-hover:bg-gold-50 group-hover:border-gold/40 transition-colors">
                                <Mail className="w-4 h-4 text-gold-600" />
                            </div>
                            <div>
                                <p className="text-[11px] uppercase tracking-widest2 text-ink/55">Email</p>
                                <p className="text-[15px] text-ink font-medium mt-0.5 break-all">{BRAND.email}</p>
                            </div>
                        </a>

                        <div className="flex items-start gap-4">
                            <div className="w-11 h-11 flex items-center justify-center bg-soft border border-hairline">
                                <MapPin className="w-4 h-4 text-gold-600" />
                            </div>
                            <div>
                                <p className="text-[11px] uppercase tracking-widest2 text-ink/55">Office</p>
                                <p className="text-[15px] text-ink font-medium mt-0.5">{BRAND.address}</p>
                            </div>
                        </div>

                        <a
                            href={whatsappUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-2 px-5 py-3 bg-[#25D366] text-white text-[13px] font-semibold tracking-wide hover:bg-[#1eb558] transition-colors"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Chat on WhatsApp
                        </a>
                    </div>

                    <div className="mt-10 overflow-hidden rounded-xl border border-hairline">
                        <iframe
                            title="Waylthlane Financials — Rajnagar Extension, Ghaziabad"
                            src="https://maps.google.com/maps?q=Rajnagar%20Extension%2C%20Ghaziabad%2C%20Uttar%20Pradesh&t=&z=14&ie=UTF8&iwloc=&output=embed"
                            width="100%"
                            height="260"
                            style={{ border: 0 }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                </div>

                {/* RIGHT — form */}
                <div className="lg:col-span-7">
                    <form
                        onSubmit={submit}
                        className="bg-soft border border-hairline rounded-xl p-7 lg:p-10 shadow-soft"
                    >
                        <p className="overline">Consultation Inquiry</p>
                        <h3 className="font-display-bold text-ink text-[28px] lg:text-[32px] leading-tight mt-2">
                            Tell us a little about you.
                        </h3>

                        <div className="mt-8 grid sm:grid-cols-2 gap-5">
                            <Field label="Full Name *">
                                <input
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={update('name')}
                                    data-testid={TID.contactName}
                                    placeholder="Your full name"
                                    className="input-wl"
                                />
                            </Field>
                            <Field label="Email *">
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={update('email')}
                                    data-testid={TID.contactEmail}
                                    placeholder="you@example.com"
                                    className="input-wl"
                                />
                            </Field>
                            <Field label="Phone *">
                                <input
                                    type="tel"
                                    required
                                    value={form.phone}
                                    onChange={update('phone')}
                                    data-testid={TID.contactPhone}
                                    placeholder="+91 ..."
                                    className="input-wl"
                                />
                            </Field>
                            <Field label="Interested in">
                                <select
                                    value={form.service}
                                    onChange={update('service')}
                                    data-testid={TID.contactService}
                                    className="input-wl bg-white appearance-none cursor-pointer"
                                >
                                    {SERVICE_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        <div className="mt-5">
                            <Field label="How can we help?">
                                <textarea
                                    rows={4}
                                    value={form.message}
                                    onChange={update('message')}
                                    data-testid={TID.contactMessage}
                                    placeholder="Briefly describe your goals or questions..."
                                    className="input-wl resize-none"
                                />
                            </Field>
                        </div>

                        <button
                            type="submit"
                            disabled={status.loading}
                            data-testid={TID.contactSubmit}
                            className="btn-primary mt-7 w-full sm:w-auto"
                        >
                            {status.loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    Book My Consultation
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>

                        {status.ok && (
                            <div
                                data-testid={TID.contactSuccess}
                                className="mt-5 flex items-start gap-3 p-4 bg-forest-50 border border-forest/30 text-forest text-[14px]"
                            >
                                <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
                                <span>
                                    Thank you. Your request was received — a member of our team will be in
                                    touch within one business day.
                                </span>
                            </div>
                        )}

                        {status.error && (
                            <div
                                data-testid={TID.contactError}
                                className="mt-5 flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-700 text-[14px]"
                            >
                                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                                <span>{status.error}</span>
                            </div>
                        )}

                        <p className="mt-6 text-[12px] text-ink/55 leading-relaxed">
                            By submitting this form, you agree to be contacted by Waylthlane Financials
                            regarding your inquiry. We respect your privacy and never share your details.
                        </p>
                    </form>
                </div>
            </div>

            {/* Local input styles */}
            <style>{`
                .input-wl {
                    width: 100%;
                    background: #ffffff;
                    border: 1px solid #E5E7EB;
                    padding: 12px 14px;
                    font-size: 14.5px;
                    color: #0F1A2C;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                    border-radius: 0.5rem;
                }
                .input-wl:focus {
                    outline: none;
                    border-color: #C5A059;
                    box-shadow: 0 0 0 3px rgba(197,160,89,0.15);
                }
                .input-wl::placeholder { color: #9CA3AF; }
            `}</style>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-widest2 font-semibold text-ink/65 mb-2">
                {label}
            </span>
            {children}
        </label>
    );
}
